// src/lib/costCalculations.ts
// Pure calculation functions for cost simulator

import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';

export interface CostSimulatorConfig {
  aiServiceModel: string;
  inputCostPerMToken: number;
  outputCostPerMToken: number;
  capexPerServer: number;
  powerKwPerServer: number;
  electricityPerKwh: number;
  facilitiesMonthly: number;
  personnelMonthly: number;
  adminPerServerMonthly: number;
  annualGrowthRate: number;
  gpusPerServer: number;
  workHoursPerDay: number;
  workDaysPerMonth: number;
}

export interface CostComparisonResult {
  breakEvenMonth: string | null;
  breakEvenMonthIndex: number | null;
  fiveYearOnPremTco: number;
  fiveYearCloudSpend: number;
  savings: number;
  savingsPercent: number;
}

export interface MonthlyRollupEnhanced {
  monthKey: string;
  monthIndex: number;
  // Token volumes
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  // Cloud costs
  cloudInputCost: number;
  cloudOutputCost: number;
  monthlyCloudCost: number;
  cumCloudUSD: number;
  // On-prem costs
  monthlyCapexAmortized: number;
  monthlyPowerCost: number;
  monthlyAdminCost: number;
  monthlyFacilitiesCost: number;
  monthlyPersonnelCost: number;
  monthlyOpEx: number;
  monthlyOnPremTco: number;
  cumOnPremTcoUSD: number;
  // Upfront CapEx (only month 0)
  upfrontCapex: number;
}

export const DEFAULT_COST_CONFIG: CostSimulatorConfig = {
  aiServiceModel: 'GPT-4o',
  inputCostPerMToken: 5,
  outputCostPerMToken: 15,
  capexPerServer: 100000,
  powerKwPerServer: 10.2,
  electricityPerKwh: 0.12,
  facilitiesMonthly: 5000,
  personnelMonthly: 15000,
  adminPerServerMonthly: 1500,
  annualGrowthRate: 10,
  gpusPerServer: 8,
  workHoursPerDay: 8,
  workDaysPerMonth: 22,
};

/**
 * Calculate monthly token volume from sizing snapshot throughput
 */
export function calculateMonthlyTokenVolume(
  snapshot: NewSizingSnapshot | null,
  config: CostSimulatorConfig,
  monthIndex: number = 0
): { inputTokens: number; outputTokens: number; totalTokens: number } {
  if (!snapshot) {
    return { inputTokens: 0, outputTokens: 0, totalTokens: 0 };
  }

  const { workHoursPerDay, workDaysPerMonth, annualGrowthRate } = config;
  const secondsPerMonth = workHoursPerDay * 3600 * workDaysPerMonth;
  
  // Apply growth rate (compounded monthly)
  const monthlyGrowthRate = Math.pow(1 + annualGrowthRate / 100, 1 / 12);
  const growthMultiplier = Math.pow(monthlyGrowthRate, monthIndex);

  const avgPromptTps = snapshot.avgPromptTokensPerSec ?? 0;
  const avgDecodeTps = snapshot.avgDecodeTokensPerSec ?? 0;

  const inputTokens = avgPromptTps * secondsPerMonth * growthMultiplier;
  const outputTokens = avgDecodeTps * secondsPerMonth * growthMultiplier;

  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
  };
}

/**
 * Calculate cloud costs for a given token volume
 */
export function calculateCloudCosts(
  inputTokens: number,
  outputTokens: number,
  config: CostSimulatorConfig
): { cloudInputCost: number; cloudOutputCost: number; monthlyCloudCost: number } {
  const { inputCostPerMToken, outputCostPerMToken } = config;

  const cloudInputCost = (inputTokens / 1_000_000) * inputCostPerMToken;
  const cloudOutputCost = (outputTokens / 1_000_000) * outputCostPerMToken;
  const monthlyCloudCost = cloudInputCost + cloudOutputCost;

  return { cloudInputCost, cloudOutputCost, monthlyCloudCost };
}

/**
 * Calculate on-prem TCO from server count
 */
export function calculateOnPremTco(
  serverCount: number,
  config: CostSimulatorConfig,
  amortizationMonths: number = 60
): {
  capexTotal: number;
  monthlyCapexAmortized: number;
  monthlyPowerCost: number;
  monthlyAdminCost: number;
  monthlyFacilitiesCost: number;
  monthlyPersonnelCost: number;
  monthlyOpEx: number;
  monthlyTco: number;
} {
  const {
    capexPerServer,
    powerKwPerServer,
    electricityPerKwh,
    facilitiesMonthly,
    personnelMonthly,
    adminPerServerMonthly,
    workHoursPerDay,
    workDaysPerMonth,
  } = config;

  const capexTotal = serverCount * capexPerServer;
  const monthlyCapexAmortized = capexTotal / amortizationMonths;

  // Power cost: kW × hours/day × days/month × $/kWh × servers
  // Assume servers run 24/7 even if workload is only during work hours
  const hoursPerMonth = 24 * 30; // Servers run continuously
  const monthlyPowerCost = powerKwPerServer * hoursPerMonth * electricityPerKwh * serverCount;

  const monthlyAdminCost = adminPerServerMonthly * serverCount;
  const monthlyFacilitiesCost = facilitiesMonthly;
  const monthlyPersonnelCost = personnelMonthly;

  const monthlyOpEx = monthlyPowerCost + monthlyAdminCost + monthlyFacilitiesCost + monthlyPersonnelCost;
  const monthlyTco = monthlyCapexAmortized + monthlyOpEx;

  return {
    capexTotal,
    monthlyCapexAmortized,
    monthlyPowerCost,
    monthlyAdminCost,
    monthlyFacilitiesCost,
    monthlyPersonnelCost,
    monthlyOpEx,
    monthlyTco,
  };
}

/**
 * Generate 60-month projection with cumulative costs
 * Hardware scales linearly with growth rate: 10% annual growth = 10% more servers per year
 */
export function generateMonthlyRollups(
  snapshot: NewSizingSnapshot | null,
  config: CostSimulatorConfig,
  months: number = 60
): MonthlyRollupEnhanced[] {
  const rollups: MonthlyRollupEnhanced[] = [];
  
  if (!snapshot) {
    return rollups;
  }

  const baseServerCount = Math.ceil(snapshot.finalGpuCount / config.gpusPerServer);
  const annualGrowthRate = config.annualGrowthRate / 100; // Convert percentage to decimal

  let cumCloudUSD = 0;
  let cumOnPremTcoUSD = 0;

  for (let i = 0; i < months; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() + i);
    const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });

    // Token volumes with growth (compounded monthly)
    const tokens = calculateMonthlyTokenVolume(snapshot, config, i);
    const cloud = calculateCloudCosts(tokens.inputTokens, tokens.outputTokens, config);
    cumCloudUSD += cloud.monthlyCloudCost;

    // Linear hardware growth: at start of each year, add servers proportional to growth rate
    // Year 0 (months 0-11): baseServerCount
    // Year 1 (months 12-23): baseServerCount * (1 + growthRate)
    // Year 2 (months 24-35): baseServerCount * (1 + 2*growthRate)
    // etc.
    const yearIndex = Math.floor(i / 12);
    const currentServerCount = Math.ceil(baseServerCount * (1 + yearIndex * annualGrowthRate));
    
    // Calculate this month's on-prem costs based on current server count
    const onPremCosts = calculateOnPremTco(currentServerCount, config, months);

    // CapEx: initial purchase at month 0, then incremental at start of each year
    let upfrontCapex = 0;
    if (i === 0) {
      // Year 0: full initial CapEx
      upfrontCapex = baseServerCount * config.capexPerServer;
    } else if (i % 12 === 0) {
      // Start of new year: CapEx for additional servers
      const prevYearServerCount = Math.ceil(baseServerCount * (1 + (yearIndex - 1) * annualGrowthRate));
      const newServers = currentServerCount - prevYearServerCount;
      upfrontCapex = newServers * config.capexPerServer;
    }

    // Monthly OpEx scales with current server count
    const monthlyOnPremTco = onPremCosts.monthlyOpEx;
    cumOnPremTcoUSD += upfrontCapex + monthlyOnPremTco;

    rollups.push({
      monthKey,
      monthIndex: i,
      inputTokens: tokens.inputTokens,
      outputTokens: tokens.outputTokens,
      totalTokens: tokens.totalTokens,
      cloudInputCost: cloud.cloudInputCost,
      cloudOutputCost: cloud.cloudOutputCost,
      monthlyCloudCost: cloud.monthlyCloudCost,
      cumCloudUSD,
      monthlyCapexAmortized: onPremCosts.monthlyCapexAmortized,
      monthlyPowerCost: onPremCosts.monthlyPowerCost,
      monthlyAdminCost: onPremCosts.monthlyAdminCost,
      monthlyFacilitiesCost: onPremCosts.monthlyFacilitiesCost,
      monthlyPersonnelCost: onPremCosts.monthlyPersonnelCost,
      monthlyOpEx: onPremCosts.monthlyOpEx,
      monthlyOnPremTco,
      cumOnPremTcoUSD,
      upfrontCapex,
    });
  }

  return rollups;
}

/**
 * Find break-even month where on-prem becomes cheaper than cloud
 */
export function findBreakEvenMonth(rollups: MonthlyRollupEnhanced[]): CostComparisonResult {
  let breakEvenMonth: string | null = null;
  let breakEvenMonthIndex: number | null = null;

  for (let i = 0; i < rollups.length; i++) {
    if (rollups[i].cumOnPremTcoUSD <= rollups[i].cumCloudUSD) {
      breakEvenMonth = rollups[i].monthKey;
      breakEvenMonthIndex = i;
      break;
    }
  }

  const lastMonth = rollups[rollups.length - 1];
  const fiveYearOnPremTco = lastMonth?.cumOnPremTcoUSD ?? 0;
  const fiveYearCloudSpend = lastMonth?.cumCloudUSD ?? 0;
  const savings = fiveYearCloudSpend - fiveYearOnPremTco;
  const savingsPercent = fiveYearCloudSpend > 0 ? (savings / fiveYearCloudSpend) * 100 : 0;

  return {
    breakEvenMonth,
    breakEvenMonthIndex,
    fiveYearOnPremTco,
    fiveYearCloudSpend,
    savings,
    savingsPercent,
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number, compact: boolean = false): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: compact && Math.abs(value) >= 1000000 ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  }).format(value);
}

/**
 * Format large numbers with K/M/B suffixes
 */
export function formatTokens(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}
